import React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useTranslation } from "react-i18next";
import {
  Link2,
  ShieldCheck,
  FilePlus2,
  X,
  Landmark,
  MapPin,
  ChevronDown,
} from "lucide-react";

import { useToast } from "../../../../../components/feedback/ToastProvider";

import "./CreateLoanCaseModal.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

export default function CreateLoanCaseModal({
  isClosing = false,
  isCreatingManual = false,
  manualCreateError = "",

  marketRegions = [],
  selectedMarketRegionId = "",
  onMarketRegionChange,

  onClose,
  onCreateManual,
  onLinkedCreated,
}) {
  const { t } = useTranslation("lender-loan-cases");

  const { getAccessTokenSilently } = useAuth0();

  const { showToast } = useToast();

  const [referenceCode, setReferenceCode] = React.useState("");

  const [isLinking, setIsLinking] = React.useState(false);

  /* =========================================================
     REGION
     ========================================================= */

  const hasMarketRegions = marketRegions.length > 0;

  const hasMultipleMarketRegions = marketRegions.length > 1;

  const selectedMarketRegion = React.useMemo(
    () =>
      marketRegions.find(
        (region) => String(region.id) === String(selectedMarketRegionId),
      ) || null,
    [marketRegions, selectedMarketRegionId],
  );

  React.useEffect(() => {
    if (marketRegions.length === 1 && !selectedMarketRegionId) {
      onMarketRegionChange?.(String(marketRegions[0].id));
    }
  }, [marketRegions, selectedMarketRegionId, onMarketRegionChange]);

  /* =========================================================
     CLOSE
     ========================================================= */

  function closeWithAnimation() {
    if (isLinking || isCreatingManual) {
      return;
    }

    onClose?.();
  }

  /* =========================================================
     LINK EXISTING DOSSIER
     ========================================================= */

  async function handleLinkReference() {
    const cleanReference = referenceCode.trim();

    if (!cleanReference) {
      showToast({
        type: "error",

        title: t("createLoanCaseModal.toast.referenceMissingTitle"),

        message: t("createLoanCaseModal.toast.referenceMissingMessage"),
      });

      return;
    }

    try {
      setIsLinking(true);

      const token = await getAccessTokenSilently();

      const response = await fetch(
        `${API_BASE}/api/lender/loan-cases/link-reference/`,
        {
          method: "POST",

          headers: {
            Authorization: `Bearer ${token}`,

            "Content-Type": "application/json",
          },

          body: JSON.stringify({
            reference_code: cleanReference,
          }),
        },
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        if (data.code === "REFERENCE_NOT_FOUND") {
          throw new Error("REFERENCE_NOT_FOUND");
        }

        if (data.code === "ALREADY_LINKED") {
          throw new Error("ALREADY_LINKED");
        }

        if (data.code === "DOSSIER_NOT_AVAILABLE") {
          throw new Error("DOSSIER_NOT_AVAILABLE");
        }

        if (data.code === "MARKET_REGION_MISSING") {
          throw new Error("MARKET_REGION_MISSING");
        }

        if (data.code === "MARKET_REGION_NOT_ASSIGNED") {
          throw new Error("MARKET_REGION_NOT_ASSIGNED");
        }

        throw new Error(data.detail || "LINK_REFERENCE_FAILED");
      }

      const createdLoanCase = data.loan_case;

      showToast({
        type: "success",

        title: t("createLoanCaseModal.toast.linkSuccessTitle"),

        message: t("createLoanCaseModal.toast.linkSuccessMessage", {
          reference: createdLoanCase?.case_reference || cleanReference,
        }),
      });

      onLinkedCreated?.(createdLoanCase);
    } catch (error) {
      let title = t("createLoanCaseModal.toast.linkErrorTitle");

      let message = t("createLoanCaseModal.toast.linkErrorMessage");

      if (error.message === "REFERENCE_NOT_FOUND") {
        title = t("createLoanCaseModal.toast.referenceNotFoundTitle");

        message = t("createLoanCaseModal.toast.referenceNotFoundMessage");
      }

      if (error.message === "ALREADY_LINKED") {
        title = t("createLoanCaseModal.toast.alreadyLinkedTitle");

        message = t("createLoanCaseModal.toast.alreadyLinkedMessage");
      }

      if (error.message === "DOSSIER_NOT_AVAILABLE") {
        title = t("createLoanCaseModal.toast.notAvailableTitle");

        message = t("createLoanCaseModal.toast.notAvailableMessage");
      }

      if (error.message === "MARKET_REGION_MISSING") {
        title = t(
          "createLoanCaseModal.toast.marketRegionMissingTitle",
          "Market region unavailable",
        );

        message = t(
          "createLoanCaseModal.toast.marketRegionMissingMessage",
          "The dossier does not have a market region configured.",
        );
      }

      if (error.message === "MARKET_REGION_NOT_ASSIGNED") {
        title = t(
          "createLoanCaseModal.toast.marketRegionNotAssignedTitle",
          "Market not available",
        );

        message = t(
          "createLoanCaseModal.toast.marketRegionNotAssignedMessage",
          "Your lender organisation is not assigned to this dossier's market region.",
        );
      }

      showToast({
        type: "error",
        title,
        message,
      });
    } finally {
      setIsLinking(false);
    }
  }

  /* =========================================================
     CREATE MANUAL CASE
     ========================================================= */

  async function handleCreateManual() {
    if (isLinking || isCreatingManual) {
      return;
    }

    if (!selectedMarketRegionId) {
      showToast({
        type: "error",

        title: t(
          "createLoanCaseModal.manual.marketRegionRequiredTitle",
          "Select a market region",
        ),

        message: t(
          "createLoanCaseModal.manual.marketRegionRequiredMessage",
          "Select the market region for this manual loan case before continuing.",
        ),
      });

      return;
    }

    if (typeof onCreateManual !== "function") {
      return;
    }

    await onCreateManual();
  }

  /* =========================================================
     KEYBOARD
     ========================================================= */

  function handleReferenceKeyDown(event) {
    if (event.key === "Enter" && !isLinking) {
      event.preventDefault();

      handleLinkReference();
    }
  }

  /* =========================================================
     RENDER
     ========================================================= */

  return (
    <div
      className={`loan-case-create-modal ${
        isClosing ? "loan-case-create-modal--closing" : ""
      }`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-loan-case-title"
    >
      <div
        className="loan-case-create-modal__backdrop"
        onClick={closeWithAnimation}
      />

      <section className="loan-case-create-modal__dialog">
        <button
          type="button"
          className="loan-case-create-modal__close"
          onClick={closeWithAnimation}
          disabled={isLinking || isCreatingManual}
          aria-label={t("createLoanCaseModal.close")}
        >
          <X size={16} />
        </button>

        {/* ===================================================
            HEADER
            =================================================== */}

        <div className="loan-case-create-modal__kicker">
          <Landmark size={15} />

          {t("createLoanCaseModal.kicker")}
        </div>

        <div className="loan-case-create-modal__header">
          <div className="loan-case-create-modal__icon">
            <ShieldCheck size={24} />
          </div>

          <div>
            <h2 id="create-loan-case-title">
              {t("createLoanCaseModal.title")}
            </h2>

            <p>{t("createLoanCaseModal.subtitle")}</p>
          </div>
        </div>

        {/* ===================================================
            OPTIONS
            =================================================== */}

        <div className="loan-case-create-modal__options">
          {/* =================================================
              LINK EXISTING DOSSIER
              ================================================= */}

          <article className="loan-case-create-modal__option">
            <div className="loan-case-create-modal__option-top">
              <div className="loan-case-create-modal__option-icon">
                <Link2 size={19} />
              </div>

              <span>{t("createLoanCaseModal.reference.badge")}</span>
            </div>

            <h3>{t("createLoanCaseModal.reference.title")}</h3>

            <p>{t("createLoanCaseModal.reference.description")}</p>

            <div className="loan-case-create-modal__input-row">
              <input
                value={referenceCode}
                onChange={(event) => setReferenceCode(event.target.value)}
                onKeyDown={handleReferenceKeyDown}
                placeholder={t("createLoanCaseModal.reference.placeholder")}
                disabled={isLinking || isCreatingManual}
                autoFocus
                autoComplete="off"
              />

              <button
                type="button"
                onClick={handleLinkReference}
                disabled={
                  isLinking || isCreatingManual || !referenceCode.trim()
                }
              >
                {isLinking
                  ? t("createLoanCaseModal.reference.loading")
                  : t("createLoanCaseModal.reference.button")}
              </button>
            </div>
          </article>

          {/* =================================================
              MANUAL ENTRY
              ================================================= */}

          <article className="loan-case-create-modal__option loan-case-create-modal__option--primary">
            <div className="loan-case-create-modal__option-top">
              <div className="loan-case-create-modal__option-icon">
                <FilePlus2 size={19} />
              </div>

              <span>{t("createLoanCaseModal.manual.badge")}</span>
            </div>

            <h3>{t("createLoanCaseModal.manual.title")}</h3>

            <p>{t("createLoanCaseModal.manual.description")}</p>

            {/* ===============================================
                MARKET REGION
                =============================================== */}

            <div className="loan-case-create-modal__market-region">
              <label
                className="loan-case-create-modal__market-region-label"
                htmlFor="manual-market-region"
              >
                <MapPin size={15} />

                <span>
                  {t(
                    "createLoanCaseModal.manual.marketRegion",
                    "Market region",
                  )}
                </span>

                <span className="loan-case-create-modal__required">*</span>
              </label>

              {!hasMarketRegions ? (
                <div className="loan-case-create-modal__market-region-empty">
                  {t(
                    "createLoanCaseModal.manual.noMarketRegions",
                    "No active market regions are assigned to your lender organisation.",
                  )}
                </div>
              ) : hasMultipleMarketRegions ? (
                <div className="loan-case-create-modal__select-wrap">
                  <select
                    id="manual-market-region"
                    className="loan-case-create-modal__select"
                    value={selectedMarketRegionId}
                    onChange={(event) =>
                      onMarketRegionChange?.(event.target.value)
                    }
                    disabled={isLinking || isCreatingManual}
                  >
                    <option value="">
                      {t(
                        "createLoanCaseModal.manual.selectMarketRegion",
                        "Select market region",
                      )}
                    </option>

                    {marketRegions.map((region) => (
                      <option key={region.id} value={region.id}>
                        {region.name}
                        {region.country_code ? ` · ${region.country_code}` : ""}
                      </option>
                    ))}
                  </select>

                  <ChevronDown
                    size={16}
                    className="loan-case-create-modal__select-icon"
                  />
                </div>
              ) : (
                <div className="loan-case-create-modal__market-region-selected">
                  <MapPin size={15} />

                  <div>
                    <strong>
                      {selectedMarketRegion?.name || marketRegions[0]?.name}
                    </strong>

                    {selectedMarketRegion?.country_code ||
                    marketRegions[0]?.country_code ? (
                      <span>
                        {selectedMarketRegion?.country_code ||
                          marketRegions[0]?.country_code}
                      </span>
                    ) : null}
                  </div>
                </div>
              )}

              <p className="loan-case-create-modal__market-region-helper">
                {t(
                  "createLoanCaseModal.manual.marketRegionHelper",
                  "The selected market determines the country-specific loan workflow and available renovation funding sources.",
                )}
              </p>
            </div>

            {/* ===============================================
                CREATE
                =============================================== */}

            <button
              type="button"
              className="loan-case-create-modal__primary-btn"
              onClick={handleCreateManual}
              disabled={
                isLinking ||
                isCreatingManual ||
                !hasMarketRegions ||
                !selectedMarketRegionId
              }
            >
              {isCreatingManual
                ? t(
                    "createLoanCaseModal.manual.creating",
                    "Creating loan case...",
                  )
                : t("createLoanCaseModal.manual.button")}
            </button>

            {manualCreateError ? (
              <div className="loan-case-create-modal__error" role="alert">
                {manualCreateError}
              </div>
            ) : null}
          </article>
        </div>

        {/* ===================================================
            NOTICE
            =================================================== */}

        <div className="loan-case-create-modal__notice">
          {t("createLoanCaseModal.notice")}
        </div>
      </section>
    </div>
  );
}
